// src/components/FilterPanel.tsx
import React, { useState, useEffect, useRef } from "react";
import { Filter, Check, X, AlertTriangle, FileText } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { FilterState } from "@/types";

interface FilterPanelProps {
  processData: any[];
  onFilterChange: (filters: FilterState) => void;
  iconsOnly?: boolean; // Ny prop för att visa endast ikoner
}

const FilterPanel: React.FC<FilterPanelProps> = ({ 
  processData, 
  onFilterChange,
  iconsOnly = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    roles: [],
    documentTypes: [],
    showOnlyMyTasks: false,
    currentUserRole: undefined,
    showImportantOnly: false,
    showWithDocumentsOnly: false
  });
  
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Detektera mobilvy baserat på fönsterstorlek
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Stäng filterpanelen när användaren klickar utanför (utan extern hook)
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  
  // Extrahera unika roller från processData
  useEffect(() => {
    if (processData && processData.length > 0) {
      // Samla alla unika roller från alla process-objekt
      const allRoles: string[] = [];
      
      // För varje processobjekt
      processData.forEach(item => {
        // Lägg till roller från Deltagareochansvar-fälten
        if (item.Deltagareochansvar1) {
          const rolesFromField1 = extractRolesFromString(item.Deltagareochansvar1);
          allRoles.push(...rolesFromField1);
        }
        
        if (item.Deltagareochansvar2) {
          const rolesFromField2 = extractRolesFromString(item.Deltagareochansvar2);
          allRoles.push(...rolesFromField2);
        }
        
        // Lägg till roller från field_5 (backward compatibility)
        if (item.field_5) {
          const rolesFromField5 = item.field_5.split(';').map((r: string) => r.trim()).filter(Boolean);
          allRoles.push(...rolesFromField5);
        }
      });
      
      // Filtrera och sortera unika roller
      const uniqueRoles = [...new Set(allRoles)]
        .filter(role => role && role.length > 0)
        .sort();
      
      setAvailableRoles(uniqueRoles);
    }
  }, [processData]);
  
  // Hjälpfunktion för att extrahera roller från en deltagarsträng
  const extractRolesFromString = (participantString: string): string[] => {
    if (!participantString) return [];
    
    return participantString
      .split(';')
      .map(part => {
        const colonIndex = part.indexOf(':');
        if (colonIndex === -1) return part.trim();
        return part.substring(0, colonIndex).trim();
      })
      .filter(Boolean);
  };
  
  // Uppdatera filters och meddela föräldrakomponenten
  const updateFilters = (newFilters: Partial<FilterState>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };
  
  // Växla rollfilter
  const toggleRoleFilter = (role: string) => {
    const currentRoles = [...filters.roles];
    if (currentRoles.includes(role)) {
      updateFilters({ roles: currentRoles.filter(r => r !== role) });
    } else {
      updateFilters({ roles: [...currentRoles, role] });
    }
  };
  
  // Växla viktigt-endast-filter
  const toggleImportantFilter = () => {
    updateFilters({ showImportantOnly: !filters.showImportantOnly });
  };
  
  // Växla dokument-endast-filter
  const toggleDocumentsFilter = () => {
    updateFilters({ showWithDocumentsOnly: !filters.showWithDocumentsOnly });
  };
  
  // Återställ alla filter
  const resetFilters = () => {
    const resetState = {
      roles: [],
      documentTypes: [],
      showOnlyMyTasks: false,
      currentUserRole: undefined,
      showImportantOnly: false,
      showWithDocumentsOnly: false
    };
    setFilters(resetState);
    onFilterChange(resetState);
    
    // I mobilvy, stäng panelen efter återställning
    if (isMobile) {
      setIsOpen(false);
    }
  };
  
  // Beräkna om det finns aktiva filter
  const hasActiveFilters = filters.roles.length > 0 || 
                           filters.showImportantOnly || 
                           filters.showWithDocumentsOnly;
  
  // Räkna antalet aktiva filter
  const getActiveFilterCount = () => {
    let count = filters.roles.length;
    if (filters.showImportantOnly) count++;
    if (filters.showWithDocumentsOnly) count++;
    return count;
  };

  // Gemensam style för header-knappar med endast ikoner
  const headerButtonStyle = "bg-gray-800/50 text-gray-300 border border-gray-700 hover:bg-orange-300/10 hover:text-orange-300 transition-colors flex items-center justify-center h-9 w-9 p-0";
  
  return (
    <div className="relative" ref={filterPanelRef}>
      {iconsOnly ? (
        // Version med endast ikon
        <Button 
          onClick={() => setIsOpen(!isOpen)}
          variant="outline" 
          size="icon"
          className={`${headerButtonStyle} ${
            hasActiveFilters ? "border-green-500/50" : ""
          }`}
          aria-label="Filter"
        >
          <Filter className="h-4 w-4" />
          {hasActiveFilters && (
            <Badge 
              className="absolute -top-1 -right-1 min-w-4 h-4 flex items-center justify-center bg-green-500/50 text-green-300 p-0 rounded-full text-[10px]" 
              aria-label={`${getActiveFilterCount()} aktiva filter`}
            >
              {getActiveFilterCount()}
            </Badge>
          )}
        </Button>
      ) : (
        // Standard version med text
        <Button 
          onClick={() => setIsOpen(!isOpen)} 
          variant="outline" 
          size="sm"
          className={` w-full bg-gray-800/50 text-gray-300 border border-gray-700 hover:bg-orange-300/10 hover:text-orange-300 transition-colors ${
            hasActiveFilters ? "border-green-500/50" : ""
          }`}
        >
          <Filter className="h-4 w-4 mr-2" />
          <span>Filter</span>
          {hasActiveFilters && (
            <Badge className="ml-1 bg-green-500/20 text-green-300 border-green-500/30">
              {getActiveFilterCount()}
            </Badge>
          )}
        </Button>
      )}
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`absolute ${isMobile ? 'left-0' : 'right-0'} top-full mt-2 z-50 w-full md:w-80 lg:w-96`}
          >
            <Card className="bg-gray-800/95 border-gray-700 backdrop-blur-md">
              <div className="p-3 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-white font-medium">Filtrera innehåll</h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsOpen(false)}
                  className="h-8 w-8 p-0 rounded-full text-white hover:bg-gray-700/70"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <CardContent className="p-4">
                {/* Filtervärden för Viktigt och Med dokument */}
                <div className="space-y-4 mb-6">
                  <h4 className="text-gray-200 font-medium mb-2">Visa endast</h4>
                  
                  {/* Filter för att visa endast viktiga moment */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-4 w-4 text-red-400" />
                      <Label htmlFor="important-filter" className="text-gray-300">
                        Markerade som viktiga
                      </Label>
                    </div>
                    <Switch
                      id="important-filter"
                      checked={filters.showImportantOnly}
                      onCheckedChange={toggleImportantFilter}
                      className={filters.showImportantOnly ? "bg-red-400" : ""}
                    />
                  </div>
                  
                  {/* Filter för att visa endast moment med dokument */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-blue-400" />
                      <Label htmlFor="documents-filter" className="text-gray-300">
                        Innehåller dokument
                      </Label>
                    </div>
                    <Switch
                      id="documents-filter"
                      checked={filters.showWithDocumentsOnly}
                      onCheckedChange={toggleDocumentsFilter}
                      className={filters.showWithDocumentsOnly ? "bg-blue-400" : ""}
                    />
                  </div>
                </div>
                
                {/* Filter efter roller */}
                <div className="mb-6">
                  <h4 className="text-gray-200 font-medium mb-2">Filtrera på specifika roller</h4>
                  <div className="text-xs text-gray-400 mb-2">
                    Välj en eller flera roller att filtrera på
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {availableRoles.map((role) => (
                      <Badge
                        key={role}
                        variant="outline"
                        className={`cursor-pointer transition-colors ${
                          filters.roles.includes(role)
                            ? "bg-[rgba(180,237,185,0.2)] text-[rgb(180,237,185)] border-[rgba(180,237,185,0.4)]"
                            : "bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700 hover:text-gray-200"
                        }`}
                        onClick={() => toggleRoleFilter(role)}
                      >
                        {role}
                        {filters.roles.includes(role) && (
                          <Check className="h-3 w-3 ml-1" />
                        )}
                      </Badge>
                    ))}

                    {availableRoles.length === 0 && (
                      <div className="text-gray-400 italic text-sm py-2">
                        Inga roller hittades i processdatan
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Återställ filter */}
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-gray-300 border-gray-700 hover:bg-gray-700 hover:text-white"
                    onClick={resetFilters}
                    disabled={!hasActiveFilters}
                  >
                    Återställ filter
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FilterPanel;